import {
  add,
  sub,
  format,
  getHours,
  getMonth,
  getYear,
  isValid,
  parse,
  setDate,
  setMonth,
  setYear,
  endOfWeek,
  getSeconds,
  startOfDay,
  getMinutes,
  startOfYear,
  startOfWeek,
  startOfHour,
  startOfMonth,
  startOfMinute,
  startOfSecond,
  getDaysInMonth,
  eachDayOfInterval,
  setSeconds,
  setMinutes,
  setHours,
  getMilliseconds,
  setMilliseconds
} from 'date-fns';
import { Types } from './enums';
import { isType } from './utils/typeUtils';

/**
 * Represents a date and time.
 */
class DateTime {
  /**
   * Initializes a new instance of the DateTime class.
   * @param {DateTime|String} value The initial value of the date time.
   */
  constructor(value) {
    if (value?.toString() === 'DateTime') {
      this._date = value._date;
    } else if (isType(value, Types.string)) {
      const dateSplit = value.split('-');
      dateSplit[1] = dateSplit[1][0] === '0' ? dateSplit[1]?.replace('0', '') : dateSplit[1];
      dateSplit[2] = dateSplit[2][0] === '0' ? dateSplit[2]?.replace('0', '') : dateSplit[2];

      this._date = new Date(dateSplit.join('-'));
    } else {
      this._date = new Date();
    }
  }

  /**
   * Adds time to the current DateTime.
   * @param {Number} amount The amount to add to the date time.
   * @param {String} unit The unit to add to (e.g. years, months, weeks, days, hours, minutes, seconds).
   * @returns DateTime
   */
  add(amount, unit) {
    const duration = {};
    duration[unit] = amount;

    this._date = add(this._date, duration);

    return this;
  }

  /**
   * Formats the current DateTime.
   * @param {String} token The token used for formatting.
   * @returns String
   */
  format(token = String.empty) {
    return format(this._date, token);
  }

  /**
   * Returns the date from the current DateTime.
   * @returns Number
   */
  getDate() {
    return parseInt(format(this._date, 'd'));
  }

  /**
   * Returns the number of days in a month for the current DateTime.
   * @returns Array
   */
  getDaysInMonth() {
    return getDaysInMonth(this._date);
  }

  static _getDaysOfWeek() {
    const dateNow = new DateTime().toDate();
    const daysOfWeek = eachDayOfInterval({
      start: startOfWeek(dateNow, { weekStartsOn: 0 }),
      end: endOfWeek(dateNow, { weekStartsOn: 0 })
    });

    return daysOfWeek;
  }

  /**
   * Returns the hour from the current DateTime.
   * @returns Number
   */
  getHour() {
    return getHours(this._date);
  }

  /**
   * Returns the miliseconds from the current DateTime.
   * @returns Number
   */
  getMiliseconds() {
    return getMilliseconds(this._date);
  }

  /**
   * Returns the minutes from the current DateTime.
   * @returns Number
   */
  getMinutes() {
    return getMinutes(this._date);
  }

  /**
   * Returns the current month of the year.
   * @returns Number
   */
  getMonth() {
    return getMonth(this._date) + 1;
  }

  /**
   * Returns all of the months as strings.
   * @returns Array
   */
  static getMonths() {
    const months = [];
    const dateTime = new DateTime();
    const startDateTime = dateTime.startOf('year');

    for (let index = 0; index < 12; ++index) {
      const month = startDateTime.format('MMMM');

      months.push(month);

      startDateTime.add(1, 'months');
    }

    return months;
  }

  /**
   * Returns the seconds from the current the DateTime.
   * @returns Number
   */
  getSeconds() {
    return getSeconds(this._date);
  }

  /**
   * Returns the year from the current the DateTime.
   * @returns Number
   */
  getYear() {
    return getYear(this._date);
  }

  /**
   * Returns a value that determines whether the provided date is valid.
   * @param {String} date The date to validate.
   * @param {String} format The date format.
   * @returns Boolean
   */
  static isValid(date, format = 'yyyy-MM-dd') {
    const parsedDate = parse(date, format, new Date());
    return isValid(parsedDate);
  }

  /**
   * Returns the current DateTime.
   * @returns DateTime
   */
  static now() {
    return new DateTime(new Date());
  }

  /**
   * Sets the date of the current DateTime.
   * @param {Number} date The date to set.
   * @returns DateTime
   */
  setDate(date) {
    this._date = setDate(this._date, date);

    return this;
  }

  /**
   * Sets the hour of the current DateTime.
   * @param {Number} hour The hour to set.
   * @returns DateTime
   */
  setHour(hour) {
    this._date = setHours(this._date, hour);

    return this;
  }

  /**
   * Sets the miliseconds of the current DateTime.
   * @param {Number} miliseconds The miliseconds to set.
   */
  setMiliseconds(miliseconds) {
    this._date = setMilliseconds(this._date, miliseconds);
  }

  /**
   * Sets the minutes of the current DateTime.
   * @param {Number} minutes The minutes to set.
   * @returns DateTime
   */
  setMinutes(minutes) {
    this._date = setMinutes(this._date, minutes);

    return this;
  }

  /**
   * Sets the current month of the current DateTime.
   * @param {Number} month The month to set.
   * @returns DateTime
   */
  setMonth(month) {
    this._date = setMonth(this._date, month);

    return this;
  }

  /**
   * Sets the current seconds of the current DateTime.
   * @param {Number} seconds The seconds to set.
   * @returns DateTime
   */
  setSeconds(seconds) {
    this._date = setSeconds(this._date, seconds);

    return this;
  }

  /**
   * Sets the current year of the current DateTime.
   * @param {Number} year The year to set.
   * @returns {DateTime} The current instance.
   */
  setYear(year) {
    this._date = setYear(this._date, year);

    return this;
  }

  /**
   * Sets the current DateTime to the start of the provided unit time.
   * @param {String} unit The unit to set.
   * @returns DateTime
   */
  startOf(unit) {
    const unitToLowerCase = unit.toLowerCase();

    if (unitToLowerCase === 'day') {
      this._date = startOfDay(this._date);
    } else if (unitToLowerCase === 'month') {
      this._date = startOfMonth(this._date);
    } else if (unitToLowerCase === 'year') {
      this._date = startOfYear(this._date);
    } else if (unitToLowerCase === 'week') {
      this._date = startOfWeek(this._date);
    } else if (unitToLowerCase === 'hour') {
      this._date = startOfHour(this._date);
    } else if (unitToLowerCase === 'minute') {
      this._date = startOfMinute(this._date);
    } else if (unitToLowerCase === 'second') {
      this._date = startOfSecond(this._date);
    }

    return this;
  }

  /**
   * Subtracts time from the current date time.
   * @param {Number} amount The amount to subtract from the date time.
   * @param {String} unit The unit to subtract from (e.g. years, months, weeks, days, hours, minutes, seconds).
   * @returns DateTime
   */
  subtract(amount, unit) {
    const duration = {};
    duration[unit] = amount;

    this._date = sub(this._date, duration);

    return this;
  }

  /**
   * Returns the underlying Date.
   * @returns Date.
   */
  toDate() {
    return this._date;
  }

  /**
   * Returns a Date as UTC.
   * @returns Date
   */
  toUtcDate() {
    return new Date(
      Date.UTC(
        this.getYear(),
        this.getMonth(),
        this.getDate(),
        this.getHour(),
        this.getMinutes(),
        this.getSeconds(),
        this.getMiliseconds()
      )
    );
  }

  /**
   * Returns a string representing the current date time in UTC.
   * @returns String
   */
  toUtcString() {
    return this._date.toUTCString();
  }

  /**
   * Returns the days of the week as strings.
   * @returns Array
   */
  static weekdays() {
    const daysOfWeek = this._getDaysOfWeek();
    const weekdays = daysOfWeek.map(day => format(day, 'EEEE'));

    return weekdays;
  }

  /**
   * Retruns the short days of the week as strings.
   * @returns Array
   */
  static weekdaysShort() {
    const daysOfWeek = this._getDaysOfWeek();
    const weekdays = daysOfWeek.map(day => format(day, 'EEE'));

    return weekdays;
  }
}

/**
 * Returns a string representing the type.
 * @returns String
 */
DateTime.prototype.toString = function toString() {
  return 'DateTime';
};

export default DateTime;
